import React, { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import DossierFormulaireContext from './DossierFormulaireContext';
import { DossierFormulaire, useDossierFormulaire } from '../hooks';

export type DossierFormulaireProps = {
    idDossiers: Array<number>;
};

const DossierFormulaireProvider: React.FunctionComponent<
    PropsWithChildren<DossierFormulaireProps>
> = function ({ idDossiers, children }) {
    const { getFormaliteFormulaire, setDossierPrestation } = useDossierFormulaire();
    const [dataLoaded, setDataLoaded] = useState<boolean>(false);
    const [hasNonPersistedChanges, setHasNonPersistedChanges] = useState<boolean>(false);
    const [processing, setProcessing] = useState<boolean>(false);
    const [currentPrestationId, setCurrentPrestationId] = useState<number>(0);
    const [formulaires, setFormulaires] = useState<Array<DossierFormulaire>>([]);

    const getCurrentFormulaire = useCallback(
        (): DossierFormulaire | undefined =>
            formulaires.find((formulaire) => formulaire.prestationId === currentPrestationId),
        [formulaires, currentPrestationId],
    );

    const persistAllFormulaires = useCallback(
        async (): Promise<void> =>
            new Promise((resolve, reject) => {
                setProcessing(true);
                const promises = formulaires.map((formulaire) => setDossierPrestation(formulaire));

                Promise.all(promises)
                    .then(() => {
                        if (hasNonPersistedChanges) {
                            setHasNonPersistedChanges(false);
                        }
                        resolve();
                    })
                    .catch(reject)
                    .finally(() => setProcessing(false));
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [formulaires, hasNonPersistedChanges],
    );

    const updateFormulaire = useDebouncedCallback(async (formulaire: DossierFormulaire) => {
        if (!hasNonPersistedChanges) {
            setHasNonPersistedChanges(true);
        }
        const buffer = formulaires.map((formInState) => {
            if (formInState.prestationId === formulaire.prestationId) {
                return formulaire;
            }

            return formInState;
        });
        setFormulaires(buffer);
    }, 500);

    const contextValue = useMemo(
        () => ({
            currentPrestationId,
            formulaires,
            dataLoaded,
            hasNonPersistedChanges,
            processing,
            setCurrentPrestationId,
            getCurrentFormulaire,
            persistAllFormulaires,
            updateFormulaire,
            setDataLoaded,
        }),
        [
            currentPrestationId,
            formulaires,
            dataLoaded,
            hasNonPersistedChanges,
            processing,
            setCurrentPrestationId,
            getCurrentFormulaire,
            persistAllFormulaires,
            updateFormulaire,
            setDataLoaded,
        ],
    );

    useEffect(() => {
        setDataLoaded(false);
        const promises = idDossiers.map((idDossier) => getFormaliteFormulaire(idDossier));
        Promise.all(promises).then((responses) => {
            const forms = responses.flat();
            setFormulaires(forms);
            setCurrentPrestationId(forms[0]?.prestationId || 0);
            setDataLoaded(true);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idDossiers]);

    return (
        <DossierFormulaireContext.Provider value={contextValue}>
            {children}
        </DossierFormulaireContext.Provider>
    );
};

export default DossierFormulaireProvider;
